import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import StatusBadge from '../components/StatusBadge';
import { useLanguage } from '../context/LanguageContext';

const CustomerDashboard = () => {
  const { t } = useLanguage();
  const [complaints, setComplaints] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [subAreas, setSubAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterArea, setFilterArea] = useState('');

  // New Complaint Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Technical Issue');
  const [priority, setPriority] = useState('Medium');
  const [departmentId, setDepartmentId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [subAreaId, setSubAreaId] = useState('');
  const [complaintImage, setComplaintImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const fetchComplaints = useCallback(async () => {
    try {
      let url = '/complaints?';
      if (filterStatus) url += `status=${encodeURIComponent(filterStatus)}&`;
      if (filterArea) url += `area_id=${encodeURIComponent(filterArea)}&`;
      const res = await api.get(url);
      setComplaints(res.data);
    } catch (err) {
      console.error('Error loading complaints:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterArea]);

  useEffect(() => {
    fetchComplaints();
    api.get('/departments').then(res => setDepartments(res.data)).catch(console.error);
    api.get('/areas').then(res => setAreas(res.data)).catch(console.error);
  }, [fetchComplaints]);

  const handleAreaChange = (val) => {
    setAreaId(val);
    setSubAreaId('');
    if (val) {
      api.get(`/sub-areas?area_id=${val}`).then(res => setSubAreas(res.data)).catch(console.error);
    } else {
      setSubAreas([]);
    }
  };

  const handleCreateComplaint = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('category', category);
      formData.append('priority', priority);
      if (departmentId) formData.append('department_id', departmentId);
      if (areaId) formData.append('area_id', areaId);
      if (subAreaId) formData.append('sub_area_id', subAreaId);
      if (complaintImage) formData.append('complaint_image', complaintImage);

      const res = await api.post('/complaints', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccessMsg(`${t('complaintSubmittedSuccess')} ${t('tableTicketNum')}: ${res.data.complaint.ticket_number}`);
      setTitle('');
      setDescription('');
      setCategory('Technical Issue');
      setPriority('Medium');
      setDepartmentId('');
      setAreaId('');
      setSubAreaId('');
      setComplaintImage(null);
      setSubAreas([]);
      fetchComplaints();

      // Close Bootstrap modal
      const modalEl = document.getElementById('newComplaintModal');
      if (modalEl && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to submit complaint.');
    } finally {
      setSubmitting(false);
    }
  };

  const total = complaints.length;
  const inProgress = complaints.filter(c => ['Assigned', 'In Progress'].includes(c.status)).length;
  const resolved = complaints.filter(c => ['Resolved', 'Closed'].includes(c.status)).length;
  const escalated = complaints.filter(c => c.is_escalated).length;

  return (
    <div className="container py-4">
      {/* Welcome Banner */}
      <div className="card glass-card border-0 p-4 mb-4 bg-primary text-white overflow-hidden position-relative">
        <div className="row align-items-center">
          <div className="col-md-8 z-1">
            <span className="badge bg-white text-primary mb-2 px-3 py-1 font-monospace">{t('customerPortal')}</span>
            <h2 className="brand-font fw-bold mb-2">{t('trackSubmitComplaints')}</h2>
            <p className="mb-3 text-white-50">
              {t('customerPortalDesc')}
            </p>
            <button
              className="btn btn-light text-primary fw-bold px-4 py-2 shadow-sm rounded-pill"
              data-bs-toggle="modal"
              data-bs-target="#newComplaintModal"
            >
              <i className="bi bi-plus-circle-fill me-2"></i> {t('submitNewComplaint')}
            </button>
          </div>
          <div className="col-md-4 text-end d-none d-md-block opacity-25">
            <i className="bi bi-shield-check display-1"></i>
          </div>
        </div>
      </div>

      {successMsg && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle-fill me-2"></i> {successMsg}
          <button type="button" className="btn-close" onClick={() => setSuccessMsg('')}></button>
        </div>
      )}

      {/* Metrics Row */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <div className="card glass-card border-0 p-3">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-primary bg-opacity-10 text-primary rounded-3 p-3">
                <i className="bi bi-ticket-perforated fs-3"></i>
              </div>
              <div>
                <div className="text-muted small fw-medium">{t('totalTickets')}</div>
                <div className="fs-3 fw-bold">{total}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-lg-3">
          <div className="card glass-card border-0 p-3">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-warning bg-opacity-10 text-warning rounded-3 p-3">
                <i className="bi bi-hourglass-split fs-3"></i>
              </div>
              <div>
                <div className="text-muted small fw-medium">{t('inProgress')}</div>
                <div className="fs-3 fw-bold text-warning">{inProgress}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-lg-3">
          <div className="card glass-card border-0 p-3">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-success bg-opacity-10 text-success rounded-3 p-3">
                <i className="bi bi-check2-circle fs-3"></i>
              </div>
              <div>
                <div className="text-muted small fw-medium">{t('resolved')}</div>
                <div className="fs-3 fw-bold text-success">{resolved}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-lg-3">
          <div className="card glass-card border-0 p-3">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-danger bg-opacity-10 text-danger rounded-3 p-3">
                <i className="bi bi-exclamation-octagon fs-3"></i>
              </div>
              <div>
                <div className="text-muted small fw-medium">{t('escalated')}</div>
                <div className="fs-3 fw-bold text-danger">{escalated}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter & Complaints Table */}
      <div className="card glass-card border-0 p-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
          <h5 className="brand-font fw-bold mb-0">{t('myComplaints')}</h5>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ width: '150px' }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">{t('allStatuses')}</option>
              <option value="Submitted">{t('status_Submitted')}</option>
              <option value="Assigned">{t('status_Assigned')}</option>
              <option value="In Progress">{t('status_InProgress')}</option>
              <option value="Pending Customer">{t('status_PendingCustomer')}</option>
              <option value="Resolved">{t('status_Resolved')}</option>
              <option value="Closed">{t('status_Closed')}</option>
              <option value="Escalated">{t('status_Escalated')}</option>
            </select>

            <select
              className="form-select form-select-sm"
              style={{ width: '150px' }}
              value={filterArea}
              onChange={(e) => setFilterArea(e.target.value)}
            >
              <option value="">{t('allAreas')}</option>
              {areas.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted small mt-2">{t('loadingTickets')}</p>
          </div>
        ) : complaints.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-inbox fs-1 d-block mb-2 text-secondary"></i>
            <p className="mb-1 fw-semibold">{t('noComplaintsFound')}</p>
            <p className="small">{t('clickSubmitHint')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t('tableTicketNum')}</th>
                  <th>{t('tableTitleCat')}</th>
                  <th>{t('tableAreaLoc')}</th>
                  <th>{t('tableDept')}</th>
                  <th>{t('tablePriority')}</th>
                  <th>{t('tableStatus')}</th>
                  <th className="text-end">{t('tableAction')}</th>
                </tr>
              </thead>
              <tbody>
                {complaints.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span className="badge bg-light text-dark border font-monospace">
                        {c.ticket_number}
                      </span>
                    </td>
                    <td>
                      <div className="fw-semibold text-dark">{c.title}</div>
                      <div className="text-muted extra-small">{c.category}</div>
                    </td>
                    <td>
                      {c.area_name ? (
                        <div>
                          <div className="fw-semibold text-primary small">
                            <i className="bi bi-geo-alt-fill me-1"></i>{c.area_name} ({c.area_code})
                          </div>
                          {c.sub_area_name && (
                            <div className="text-muted extra-small"><i className="bi bi-geo-alt me-1"></i>{c.sub_area_name}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted small italic">{t('notSpecified')}</span>
                      )}
                    </td>
                    <td>{c.department_name || t('unassigned')}</td>
                    <td>
                      <StatusBadge priority={c.priority} />
                    </td>
                    <td>
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="text-end">
                      <Link to={`/complaints/${c.id}`} className="btn btn-sm btn-outline-primary">
                        {t('viewDetails')} <i className="bi bi-arrow-right ms-1"></i>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: New Complaint */}
      <div className="modal fade" id="newComplaintModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg rounded-4">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title brand-font fw-bold">{t('submitNewComplaint')}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body p-4">
              {errorMsg && (
                <div className="alert alert-danger py-2 small mb-3">{errorMsg}</div>
              )}

              <form onSubmit={handleCreateComplaint}>
                <div className="mb-3">
                  <label className="form-label fw-semibold small">{t('complaintSubject')} <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('subjectPlaceholder')}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">
                      {t('selectAreaWard')} <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select border-primary"
                      value={areaId}
                      onChange={(e) => handleAreaChange(e.target.value)}
                      required
                    >
                      <option value="">{t('chooseAreaWard')}</option>
                      {areas.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} ({a.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">
                      {t('subAreaLocation')}
                    </label>
                    <select
                      className="form-select"
                      value={subAreaId}
                      onChange={(e) => setSubAreaId(e.target.value)}
                      disabled={!areaId || subAreas.length === 0}
                    >
                      <option value="">
                        {!areaId ? t('selectAreaFirst') : subAreas.length === 0 ? t('noSubAreasDefined') : t('chooseSubArea')}
                      </option>
                      {subAreas.map((sa) => (
                        <option key={sa.id} value={sa.id}>
                          {sa.name} ({sa.code || 'Location'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">{t('targetDepartment')}</label>
                    <select
                      className="form-select"
                      value={departmentId}
                      onChange={(e) => setDepartmentId(e.target.value)}
                    >
                      <option value="">{t('chooseDepartment')}</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">{t('priorityLevel')}</label>
                    <select
                      className="form-select"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                    >
                      <option value="Low">{t('priority_Low')} (SLA: 72 Hours)</option>
                      <option value="Medium">{t('priority_Medium')} (SLA: 48 Hours)</option>
                      <option value="High">{t('priority_High')} (SLA: 24 Hours)</option>
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold small">{t('detailedDescription')}</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder={t('descriptionPlaceholder')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                  ></textarea>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold small text-primary">
                    <i className="bi bi-camera-fill me-1"></i> {t('attachComplaintPhoto')}
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => setComplaintImage(e.target.files[0] || null)}
                  />
                  <small className="text-muted extra-small">
                    {t('photoHelpText')}
                  </small>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-light rounded-pill px-4" data-bs-dismiss="modal">
                    {t('cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary-gradient px-4" disabled={submitting}>
                    {submitting ? t('submittingTicket') : t('submitTicket')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
